import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, FastForward } from 'lucide-react';
import { Screen1Hero } from './teaser/Screen1Hero';
import { Screen2Slider } from './teaser/Screen2Slider';
import { Screen3Ranking } from './teaser/Screen3Ranking';
import { Screen4Reveal } from './teaser/Screen4Reveal';
import { Screen5Memory } from './teaser/Screen5Memory';
import { Screen6Video } from './teaser/Screen6Video';
import { Screen7Input } from './teaser/Screen7Input';
import { Screen8Close } from './teaser/Screen8Close';
interface Props {
  open: boolean;
  onClose: () => void;
}
export const Teaser = ({
  open,
  onClose
}: Props) => {
  const [screen, setScreen] = useState(0);
  useEffect(() => {
    if (!open) {
      // Reset shortly after close so the next open starts fresh
      const t = setTimeout(() => setScreen(0), 400);
      return () => clearTimeout(t);
    }
  }, [open]);
  const next = () => setScreen(s => Math.min(s + 1, 7));
  const prev = () => setScreen(s => Math.max(s - 1, 0));
  const restart = () => setScreen(0);
  const skipToEnd = () => setScreen(7);
  const screens = [<Screen1Hero key="1" onNext={next} data-id="element-251" />, <Screen2Slider key="2" onNext={next} data-id="element-252" />, <Screen3Ranking key="3" onNext={next} data-id="element-253" />, <Screen4Reveal key="4" onNext={next} data-id="element-254" />, <Screen5Memory key="5" onNext={next} data-id="element-255" />, <Screen6Video key="6" onNext={next} data-id="element-256" />, <Screen7Input key="7" onNext={next} data-id="element-257" />, <Screen8Close key="8" onRestart={restart} data-id="element-258" />];
  return <AnimatePresence data-id="element-259">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-lg flex items-center justify-center p-8" onClick={onClose} data-id="element-260">
          {/* Side label */}
          <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="absolute left-12 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-start space-y-3 max-w-xs" onClick={e => e.stopPropagation()} data-id="element-261">
            <div className="flex items-center space-x-2" data-id="element-262">
              <div className="h-[1px] w-8 bg-red-500" data-id="element-263" />
              <span className="text-xs tracking-[0.3em] uppercase text-red-500 font-semibold" data-id="element-264">
                Mobile Concept
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-265">
              ERA Pulse:
              <br data-id="element-266" />
              Lean In
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed" data-id="element-267">
              A premium mobile-first internal teaser. 8 interactive moments that
              transform passive comms into active participation.
            </p>
            <div className="flex flex-wrap gap-1.5 pt-2" data-id="element-268">
              {['Video', 'Slider', 'Drag-rank', 'Peer reveal', 'Mini-game', 'Voice', 'Anonymous input', 'CTA'].map(tag => <span key={tag} className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-800 rounded-full px-2 py-0.5" data-id="element-269">
                  {tag}
                </span>)}
            </div>
          </motion.div>

          {/* Top-right controls */}
          <div className="absolute top-6 right-6 z-10 flex items-center space-x-2" onClick={e => e.stopPropagation()} data-id="element-270">
            <button onClick={skipToEnd} disabled={screen === 7} className="flex items-center space-x-1.5 h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white text-xs font-medium tracking-wide uppercase transition-colors" aria-label="Skip to end" data-id="element-271">
              <FastForward className="w-3.5 h-3.5" data-id="element-272" />
              <span data-id="element-273">Skip</span>
            </button>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors" aria-label="Close teaser" data-id="element-274">
              <X className="w-5 h-5" data-id="element-275" />
            </button>
          </div>

          {/* Side nav — Back */}
          <button onClick={e => {
        e.stopPropagation();
        prev();
      }} disabled={screen === 0} className="absolute left-1/2 -translate-x-[280px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed border border-white/10 flex items-center justify-center text-white transition-all hover:scale-105 backdrop-blur-md" aria-label="Previous screen" data-id="element-276">
            <ChevronLeft className="w-5 h-5" data-id="element-277" />
          </button>

          {/* Side nav — Next */}
          <button onClick={e => {
        e.stopPropagation();
        next();
      }} disabled={screen === 7} className="absolute left-1/2 translate-x-[232px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed border border-white/10 flex items-center justify-center text-white transition-all hover:scale-105 backdrop-blur-md" aria-label="Next screen" data-id="element-278">
            <ChevronRight className="w-5 h-5" data-id="element-279" />
          </button>

          {/* Phone frame */}
          <motion.div initial={{
        scale: 0.85,
        opacity: 0,
        y: 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="relative" onClick={e => e.stopPropagation()} data-id="element-280">
            {/* Phone bezel */}
            <div className="relative rounded-[3rem] bg-slate-900 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6),0_0_60px_rgba(220,38,38,0.15)] border border-slate-800" data-id="element-281">
              {/* Notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-3xl z-30 flex items-center justify-center" data-id="element-282">
                <div className="w-16 h-1 rounded-full bg-slate-700" data-id="element-283" />
              </div>

              <div className="relative overflow-hidden rounded-[2.4rem] bg-slate-950" style={{
            width: 380,
            height: 760
          }} data-id="element-284">
                {/* Status bar */}
                <div className="absolute top-0 inset-x-0 z-20 h-8 px-6 flex items-center justify-between text-[10px] text-white/90 font-medium pointer-events-none" data-id="element-285">
                  <span data-id="element-286">9:41</span>
                  <div className="flex items-center space-x-1" data-id="element-287">
                    <span data-id="element-288">●●●</span>
                    <span data-id="element-289">5G</span>
                    <span data-id="element-290">100%</span>
                  </div>
                </div>

                {/* Progress dots */}
                <div className="absolute top-10 left-0 right-0 z-20 flex justify-center space-x-1 px-8 pointer-events-none" data-id="element-291">
                  {screens.map((_, i) => <div key={i} className={`h-0.5 flex-1 rounded-full transition-all duration-500 ${i <= screen ? 'bg-red-500' : 'bg-white/15'}`} data-id="element-292" />)}
                </div>

                {/* Screens */}
                <AnimatePresence mode="wait" data-id="element-293">
                  <motion.div key={screen} initial={{
                opacity: 0,
                x: 30
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -30
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="absolute inset-0" data-id="element-294">
                    {screens[screen]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Footer hint */}
            <div className="text-center mt-4 text-xs text-slate-500 tracking-widest uppercase" data-id="element-295">
              Screen {screen + 1} / {screens.length}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};